import { defineStore, type Store } from "pinia";
import joi from "joi";
import md5 from "md5";
import type {
  IDefaultState,
  IDefaultStoreActions,
  IDefultStoreGetters,
  IFileState,
  IMultiSelectItem,
  IOption,
} from "@/interfaces";
import type { CategoryTypes, StoreName } from "@/types";
import helpers from "@/helpers";
import { globali18n } from "@/i18n";
import type { CategoryFragment } from "@/graphql";
import toImageList from "@/helpers/files/toImageList";
import {
  type CategoryInput,
  Container_Type,
  type DefaultResponse,
  type Maybe,
} from "@/graphql/generated-types";
import { useCategories } from "@/composables/useCategories";

export type CategoryLisType = "news-categories" | "activities-categories";

export interface ICategoryItem {
  id?: number;
  title: string;
  description: string;
  image?: string | null | undefined;
  imageList: IFileState[];
}

export interface ICategoriesState extends IDefaultState {
  item: ICategoryItem;
  areCategoriesFetched: boolean;
  categories: ReturnType<typeof useCategories>;
}

export interface ICategoryStoreActions extends IDefaultStoreActions {
  fetchCategories(): Promise<CategoryFragment[]>;
  fetchCategoriesAsOptionArray(): Promise<IOption[]>;
  fetchCategoriesAsMultiSelectArray(): Promise<IMultiSelectItem[]>;
  save(input: CategoryInput): Promise<Maybe<DefaultResponse>>;
  isCategoryNotValid(
    categories: CategoryFragment[],
    id?: number,
  ): Promise<boolean>;
  deleteItem(): Promise<Maybe<DefaultResponse>>;
}

export type CategoriesStore = Store<
  string,
  ICategoriesState,
  IDefultStoreGetters<ICategoriesState>,
  ICategoryStoreActions
>;

export const createUseStore = (id: StoreName, type: CategoryTypes) => {
  return defineStore(id, {
    state: (): ICategoriesState => ({
      tabErrors: {},
      originalHash: null,
      validation: null,
      areCategoriesFetched: false,
      categories: useCategories(type, true),
      item: {
        title: "",
        description: "",
        image: undefined,
        imageList: [],
      },
    }),
    actions: {
      currentHash(): string {
        const data = {
          ...this.getItem(),
          imageList: this.item.imageList,
        };
        return md5(JSON.stringify(data));
      },
      setOriginalHash() {
        this.originalHash = this.currentHash();
      },
      getValidationSchema(categories: CategoryFragment[], id: number = 0) {
        return joi.object({
          title: joi
            .string()
            .max(50)
            .required()
            .custom((value: string, helper: any) => {
              const exists = categories.some(
                (category) =>
                  category.title.toLowerCase() === value.toLowerCase() &&
                  id.toString() !== category.id,
              );

              if (!exists) {
                return true;
              }

              const { t } = globali18n;
              return helper.message(t("global.error_category_exists"));
            }),
          description: joi.string().max(2000).optional(),
        });
      },
      async setItem(itemFromDatabase: any) {
        const item = { ...itemFromDatabase };

        item.imageList = toImageList(item);
        item.description = itemFromDatabase.description || "";

        this.item = item;

        this.setOriginalHash();
        this.validation = null;
      },
      getItem() {
        const item = { ...this.item };

        return {
          id: item?.id?.toString(),
          title: item.title,
          image: item.image,
          description: item.description,
        };
      },
      async isCategoryNotValid(categories: CategoryFragment[], id: number = 0) {
        const schema = this.getValidationSchema(categories, id);

        this.validation = helpers.forms.validate(schema, {
          title: this.item.title,
        });

        return !this.validation.isValid;
      },
      async getCategories() {
        if (this.areCategoriesFetched) {
          return this.categories;
        }

        return await this.fetchCategories();
      },
      async fetchCategories() {
        await this.categories.refetch();
        this.areCategoriesFetched = true;
        return this.categories;
      },
      async save(input: CategoryInput) {
        return await this.categories.save(input);
      },
      async deleteItem() {
        if (!this.item.id) {
          return;
        }

        return await this.categories.deleteItem(this.item.id);
      },
      async getEntityCountOnRelatedCategory() {
        if (!this.item.id) {
          return;
        }
        return this.categories.getEntityCountOnRelatedCategory(this.item.id);
      },
      async fetchCategoriesAsOptionArray() {
        return this.categories.categoriesOptions;
      },
      async fetchCategoriesAsMultiSelectArray() {
        return this.categories.categoriesList;
      },
    },
  });
};

export const CATEGORIES_CONTAINER_TYPE_MAP: Partial<
  Record<StoreName, Container_Type>
> = {
  NEWS_CATEGORIES: Container_Type.News,
  ACTIVITIES_CATEGORIES: Container_Type.Activity,
};

export const useActivityCategoriesStore = createUseStore(
  "ACTIVITIES_CATEGORIES",
  "activities",
);
export const useAddActivityCategoriesStore = createUseStore(
  "ACTIVITIES_ADD_CATEGORIES",
  "activities",
);
export const useNewsCategoriesStore = createUseStore("NEWS_CATEGORIES", "news");
export const useAddNewsCategoriesStore = createUseStore(
  "NEWS_ADD_CATEGORIES",
  "news",
);
